import { Link } from "react-router-dom";
import { NebulaScene } from "../three/NebulaScene";
import { useAnimation } from "../hooks/useAnimation";
import "./HomePage.css";

const INDUSTRIES = [
  {
    key: "pharma",
    name: "Pharmaceutical",
    description:
      "From discovery to commercialization — ML-driven target identification, BOM correction, clinical analytics, and AI literacy programs.",
  },
  {
    key: "agriculture",
    name: "Agriculture",
    description:
      "Crop-trait prediction, agro-chemical research data pipelines, and predictive analytics platforms for high-throughput screening.",
  },
  {
    key: "biotech",
    name: "Biotechnology",
    description:
      "Knowledge engineering and generative AI tooling for small-to-mid biotech labs. Bespoke ML solutions that fit research workflows.",
  },
  {
    key: "healthcare",
    name: "Healthcare",
    description:
      "Cardiac event prediction, EHR analytics, BI dashboards. Privacy-aware AI built for the realities of clinical operations.",
  },
];

const KEYWORDS = [
  "Transform", "Accelerate", "Optimize", "Measure",
  "Innovate", "Integrate", "Deliver", "Discover",
  "Predict", "Validate", "Scale", "Automate",
];

export function HomePage() {
  const { animationState } = useAnimation();

  return (
    <>
      <section className="hero">
        {/* Back layer: ambient Three.js nebula. Only renders when motion is
            enabled — when disabled, nothing in this layer at all. */}
        {animationState === "on" && (
          <div className="hero__nebula">
            <NebulaScene />
          </div>
        )}
        {/* Mid layer: brand humanoid asset. When animation is on, the looping
            mp4 plays; when off, we swap to the static poster image. Both
            elements share the .hero__video class so the screen-blend treatment
            applies identically — the only difference is whether the figure is
            moving. */}
        {animationState === "on" ? (
          <video
            className="hero__video"
            src="/hero-humanoid.mp4"
            poster="/hero-humanoid-poster.jpg"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden="true"
          />
        ) : (
          <img
            className="hero__video"
            src="/hero-humanoid-poster.jpg"
            alt=""
            aria-hidden="true"
          />
        )}
        <div className="container hero__content">
          <p className="eyebrow">AI for life sciences</p>
          <h1 className="hero__title">
            Turn data into <span className="gradient-text">knowledge</span>.<br />
            Knowledge into <span className="gradient-text">outcomes</span>.
          </h1>
          <p className="lede hero__lede">
            We give multi-faceted attention to your business — turning data into insights, insights into actions, and actions into measurable outcomes.
          </p>
          <div className="hero__cta">
            <Link to="/services" className="btn btn--primary">Explore services</Link>
            <Link to="/about" className="btn btn--ghost">Why SilicoInformatics</Link>
          </div>
        </div>
      </section>

      <section className="section container">
        <header className="section-head">
          <p className="eyebrow">Industries we serve</p>
          <h2>Four sectors. One disciplined approach.</h2>
          <p className="lede">
            Deep domain expertise in life sciences, paired with modern AI engineering practice.
          </p>
        </header>
        <div className="grid grid--cols-2 industries">
          {INDUSTRIES.map((i) => (
            <article key={i.key} className="card card--interactive">
              <h3>{i.name}</h3>
              <p>{i.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section container wordcloud-section">
        <header className="section-head">
          <p className="eyebrow">Our practice</p>
          <h2>What we do, in twelve words.</h2>
        </header>
        <ul className="wordcloud">
          {KEYWORDS.map((w, i) => (
            <li
              key={w}
              className="wordcloud__word"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              {w}
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
